import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { sparkToast } from '@spark';
import { GlobalContext } from '@/app/providers/globalState';
import { checkDatasetExists } from '@/features/datasets/api/datasetApi';
import { detectFileFormat } from '@/features/datasets/utils/filePreviewUtils';
import {
    allowedFormatsForTaskType,
    fetchFileColumnsWithSamples,
    readDatasetFileContent,
    uploadDatasetFile,
    MAX_FILE_CHUNK_SIZE,
} from '@/features/datasets/utils/datasetUploadFlow';
import {
    LIBRARY_PREVIEW_SAMPLE_LIMIT,
    fetchLibraryDatasetSample,
    resolveLibraryPickDefaults,
} from '@/features/datasets/utils/libraryPickDefaults';
import { cloneLibraryDatasetToTask } from '@/features/datasets/utils/cloneLibraryDatasetToTask';
import {
    HF_PREVIEW_SAMPLE_LIMIT,
    fetchHfDatasetColumns,
    fetchHfDatasetSamples,
    uploadHuggingFaceDatasetToTask,
} from '@/features/datasets/utils/huggingFaceDatasetApi';
import {
    CLONE_SOURCE_BASE_MAX_LEN,
    predictedCloneNameForUsecase,
} from '@/features/datasets/utils/uploadProgress';
import { stripCloneSuffix } from '@/features/datasets/utils/datasetCloneNames';
import { TaskType } from '@/shared/domain/taskTypes';

const MAX_DATASET_NAME_LENGTH = 40;

/** One locally picked file inside a dataset/benchmark upload slot. */
export const emptyFileEntry = {
    id: null,
    file: null,
    /** Dataset name actually sent to the backend (unique, <= 40 chars, no extension). */
    uploadName: null,
    format: null,
    /** Base64 content (chunked for large files) for column/label/preview extraction. */
    content: null,
    /** Original file size; tells whether `content` holds the whole file. */
    sizeBytes: null,
    columns: [],
    columnsLoading: false,
    /** Library pick — clone on submit instead of uploading bytes. */
    fromLibrary: false,
    librarySourceName: null,
    /** Hugging Face pick — ingest on submit with Data Collection mappings. */
    fromHuggingFace: false,
    huggingFaceDatasetId: null,
    huggingFaceSplit: null,
    huggingFaceTrainSplit: null,
    huggingFaceTestSplit: null,
    /** Clone / attached dataset name used to extract labels after File handles are dropped. */
    datasetName: null,
    /** First sample record for Review Examples preview (library / Hugging Face picks). */
    sampleRecord: null,
    /** First N sample records so the preview shuffle has more than one value. */
    sampleRecords: [],
    /** Files from the same pickFiles call; used to abort a no-shared-columns batch. */
    overlapBatchId: null,
};

/** Slot shape: zero-or-more pending file entries per target. */
export const emptyUploadSlot = {
    files: [],
};

/** First pending entry — drives Languages / details forms for that target. */
export function primaryFileEntry(slot) {
    return slot?.files?.[0] ?? emptyFileEntry;
}

/** Pending File, library clone, or Hugging Face ingest that submit() still owes. */
export function isPendingUploadEntry(entry) {
    return Boolean(entry?.file || entry?.fromLibrary || entry?.fromHuggingFace);
}

function libraryPreviewLabelColumn(taskType) {
    if (taskType === TaskType.classification) return 'labels';
    if (taskType === TaskType.NER) return 'entities';
    return null;
}

function libraryPreviewDistinctTarget(taskType) {
    if (taskType === TaskType.classification) return 'classification';
    if (taskType === TaskType.NER) return 'ner_entities';
    return null;
}

function sampleRecordsOf(entry) {
    if (Array.isArray(entry?.sampleRecords) && entry.sampleRecords.length) {
        return entry.sampleRecords;
    }
    return entry?.sampleRecord ? [entry.sampleRecord] : [];
}

export function formatFileSize(bytes) {
    if (bytes == null) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function newEntryId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `file-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Normalize a cached/restored slot: migrate the previous single-file shape
 * (`file` / `uploadName` on the slot) into `{ files: [...] }`.
 */
function normalizeSlot(raw) {
    if (!raw) return { files: [] };
    if (Array.isArray(raw.files)) {
        return {
            files: raw.files.map((entry) => ({
                ...emptyFileEntry,
                ...entry,
                columnsLoading: false,
            })),
        };
    }
    // Legacy single-file slot from an in-flight wizard cache.
    if (raw.file || raw.uploadName) {
        return {
            files: [
                {
                    ...emptyFileEntry,
                    ...raw,
                    id: raw.id || newEntryId(),
                    columnsLoading: false,
                },
            ],
        };
    }
    return { files: [] };
}

/**
 * Resolve a backend-unique dataset name for the picked file: strip the
 * extension, cap at 40 chars and append a numeric suffix while a dataset
 * with that name already exists.
 */
async function resolveUploadName(rawFileName) {
    const baseName = String(rawFileName || '').split('.')[0];
    let candidate = baseName.slice(0, MAX_DATASET_NAME_LENGTH);
    let suffix = 2;
    // Bounded loop: give up after a few tries and let the backend validate.
    while (suffix <= 20 && (await checkDatasetExists(candidate))) {
        const tail = `_${suffix}`;
        candidate = `${baseName.slice(0, MAX_DATASET_NAME_LENGTH - tail.length)}${tail}`;
        suffix += 1;
    }
    return candidate;
}

/**
 * Wizard Data Collection upload state: one slot per target
 * (`dataset` / `benchmark`), each holding zero-or-more pending file entries.
 * Picking files validates formats, resolves unique dataset names, reads a
 * base64 sample and fetches columns per file; details forms use the first
 * entry. `submit` drives the shared two-phase upload flow for every pending
 * File (dataset first, then benchmark) and clears File handles on success
 * while keeping columns/content so Back navigation still shows Languages /
 * details. `initialSlots` restores that state when the step remounts.
 */
export function useWizardDataUpload({ usecase, taskType, initialSlots, isAgentic = false }) {
    const {
        setProgressStatus,
        updateUsecase,
        registerUploadAbort,
        unregisterUploadAbort,
        registerUploadGroup,
        clearCancelledUploadSuppression,
        currentSubGroup,
        refreshUsecases,
    } = useContext(GlobalContext);

    const [slots, setSlots] = useState(() => {
        if (!initialSlots) {
            return { dataset: { files: [] }, benchmark: { files: [] } };
        }
        return {
            dataset: normalizeSlot(initialSlots.dataset),
            benchmark: normalizeSlot(initialSlots.benchmark),
        };
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const labelsPreviewRetry = useRef(new Set());

    const patchEntry = useCallback((target, entryId, patch) => {
        setSlots((prev) => ({
            ...prev,
            [target]: {
                files: prev[target].files.map((entry) =>
                    entry.id === entryId ? { ...entry, ...patch } : entry
                ),
            },
        }));
    }, []);

    // Library / attached-dataset picks used to store a single sampleRecord,
    // which left Review Examples shuffle disabled. Backfill first-N samples
    // (and retry once when the selected labels column is missing from them).
    useEffect(() => {
        const clientId = usecase?.client_id;
        if (!clientId) return undefined;
        const labelColumn = libraryPreviewLabelColumn(taskType);
        const distinctTarget = libraryPreviewDistinctTarget(taskType);
        const pending = [];
        ['dataset', 'benchmark'].forEach((target) => {
            (slots[target]?.files || []).forEach((entry) => {
                const datasetName = entry.librarySourceName || entry.datasetName;
                if (!datasetName || !entry.id || entry.content || entry.fromHuggingFace) return;
                const records = sampleRecordsOf(entry);
                const needsInitial =
                    (entry.previewSampleLimit || 0) < LIBRARY_PREVIEW_SAMPLE_LIMIT;
                const missingLabel =
                    Boolean(labelColumn) &&
                    records.length > 0 &&
                    records.every((record) => record?.[labelColumn] == null);
                const retryKey = `${target}:${entry.id}:${labelColumn || ''}`;
                if (
                    !needsInitial &&
                    !(missingLabel && !labelsPreviewRetry.current.has(retryKey))
                ) {
                    return;
                }
                if (missingLabel) labelsPreviewRetry.current.add(retryKey);
                pending.push({ target, entry, datasetName });
            });
        });
        if (!pending.length) return undefined;
        pending.forEach(({ target, entry, datasetName }) => {
            patchEntry(target, entry.id, { previewSampleLimit: LIBRARY_PREVIEW_SAMPLE_LIMIT });
            fetchLibraryDatasetSample({
                clientId,
                datasetName,
                isBenchmark: target === 'benchmark',
                labelColumn,
                distinctTarget,
                limit: LIBRARY_PREVIEW_SAMPLE_LIMIT,
            })
                .then((sample) => {
                    patchEntry(target, entry.id, {
                        sampleRecord: sample.sample || entry.sampleRecord,
                        sampleRecords: sample.samples || [],
                    });
                })
                .catch((error) => {
                    console.error('Error loading library preview samples:', error);
                });
        });
        return undefined;
    }, [slots, usecase?.client_id, patchEntry, taskType]);

    /**
     * Append one or more picked files to a target. Invalid formats are
     * skipped with a toast; valid files are prepared in parallel.
     */
    const pickFiles = useCallback(
        async (target, fileList) => {
            const incoming = Array.from(fileList || []).filter(Boolean);
            if (!incoming.length) return;

            const allowed = allowedFormatsForTaskType(taskType, { isAgentic });
            const allowedText = Array.from(allowed)
                .map((f) => f.toUpperCase())
                .join(', ');

            const accepted = [];
            for (const file of incoming) {
                const format = detectFileFormat(file);
                if (!format || !allowed.has(format)) {
                    sparkToast.error(
                        `Invalid file type (${file.name}). Please upload a ${allowedText} file.`
                    );
                    continue;
                }
                accepted.push({
                    id: newEntryId(),
                    file,
                    format,
                });
            }
            if (!accepted.length) return;

            const overlapBatchId = newEntryId();

            // Claim unique upload names up front (sequential) so parallel
            // preparation can't assign the same name to two files.
            const prepared = [];
            const claimedNames = new Set(
                (slots[target]?.files || [])
                    .map((entry) => entry.uploadName)
                    .filter(Boolean)
            );
            for (const item of accepted) {
                let uploadName = await resolveUploadName(item.file.name);
                if (claimedNames.has(uploadName)) {
                    const base = uploadName;
                    let suffix = 2;
                    while (claimedNames.has(`${base}_${suffix}`) && suffix <= 20) {
                        suffix += 1;
                    }
                    const tail = `_${suffix}`;
                    uploadName = `${base.slice(0, MAX_DATASET_NAME_LENGTH - tail.length)}${tail}`;
                }
                claimedNames.add(uploadName);
                prepared.push({ ...item, uploadName });
            }

            setSlots((prev) => ({
                ...prev,
                [target]: {
                    files: [
                        ...prev[target].files,
                        ...prepared.map(({ id, file, format, uploadName }) => ({
                            ...emptyFileEntry,
                            id,
                            file,
                            format,
                            uploadName,
                            sizeBytes: file.size,
                            columnsLoading: true,
                            overlapBatchId,
                        })),
                    ],
                },
            }));

            await Promise.all(
                prepared.map(async ({ id, file, format, uploadName }) => {
                    try {
                        const content = await readDatasetFileContent(file, {
                            chunkOnly: file.size > MAX_FILE_CHUNK_SIZE,
                        });
                        patchEntry(target, id, { content });

                        const { columns, sampleRows } = await fetchFileColumnsWithSamples({
                            filename: uploadName,
                            clientId: usecase?.client_id,
                            fileContent: content,
                            fileType: format,
                        });
                        // Server-parsed first rows back the previews the
                        // browser cannot produce itself (parquet, nested
                        // agentic trajectories).
                        patchEntry(target, id, {
                            columns,
                            columnsLoading: false,
                            sampleRecords: sampleRows,
                            sampleRecord: sampleRows[0] || null,
                        });
                    } catch (error) {
                        console.error('Error preparing picked file:', error);
                        sparkToast.error(error?.message || 'Error reading file content');
                        patchEntry(target, id, { columnsLoading: false });
                    }
                })
            );
        },
        [patchEntry, taskType, isAgentic, usecase?.client_id, slots]
    );

    /** Back-compat alias used by callers that still pass a single File. */
    const pickFile = useCallback(
        (target, file) => pickFiles(target, file ? [file] : []),
        [pickFiles]
    );

    /**
     * Attach a library dataset/benchmark as a pending slot entry (no File
     * bytes). Returns resolved defaults + distinct labels for the panel to
     * autofill the details form.
     */
    const pickFromLibrary = useCallback(
        async (target, optionMeta) => {
            if (!optionMeta?.value) return null;
            const { datasetName, columns, columnMappings } = resolveLibraryPickDefaults(
                optionMeta,
                taskType
            );
            const id = newEntryId();
            const isBenchmark = target === 'benchmark';
            const distinctTarget =
                taskType === TaskType.classification
                    ? 'classification'
                    : taskType === TaskType.NER
                      ? 'ner_entities'
                      : null;

            setSlots((prev) => ({
                ...prev,
                [target]: {
                    files: [
                        ...prev[target].files,
                        {
                            ...emptyFileEntry,
                            id,
                            uploadName: datasetName,
                            librarySourceName: datasetName,
                            fromLibrary: true,
                            columns,
                            columnsLoading: Boolean(distinctTarget && columnMappings.label_column_name),
                            previewSampleLimit: LIBRARY_PREVIEW_SAMPLE_LIMIT,
                        },
                    ],
                },
            }));

            let distinctLabels = [];
            let sampleRecord = {};
            let sampleRecords = [];
            let hasEmptyLabelValues = null;
            let hasEmptyTrajectoryValues = null;
            try {
                const sample = await fetchLibraryDatasetSample({
                    clientId: usecase?.client_id,
                    datasetName,
                    isBenchmark,
                    labelColumn: columnMappings.label_column_name || null,
                    trajectoryColumn: columnMappings.trajectory_column_name || null,
                    distinctTarget: columnMappings.label_column_name ? distinctTarget : null,
                    limit: LIBRARY_PREVIEW_SAMPLE_LIMIT,
                });
                sampleRecord = sample.sample || {};
                sampleRecords = sample.samples || [];
                distinctLabels = sample.distinct_labels || [];
                hasEmptyLabelValues = sample.has_empty_label_values ?? null;
                hasEmptyTrajectoryValues = sample.has_empty_trajectory_values ?? null;
                patchEntry(target, id, {
                    sampleRecord,
                    sampleRecords,
                    columnsLoading: false,
                });
            } catch (error) {
                console.error('Error loading library sample:', error);
                patchEntry(target, id, { columnsLoading: false });
            }

            return {
                id,
                datasetName,
                columns,
                columnMappings,
                distinctLabels,
                /** Raw mappings persisted on the source dataset (may be empty);
                 * the panel merges them with fuzzy task-label matches. */
                storedLabelMappings: columnMappings.label_mappings || {},
                sampleRecord,
                sampleRecords,
                /** Empty values in the mapped label column (null = unverifiable). */
                hasEmptyLabelValues,
                /** Empty values in the mapped trajectory column (null = unverifiable). */
                hasEmptyTrajectoryValues,
            };
        },
        [patchEntry, taskType, usecase?.client_id]
    );

    /**
     * Attach a Hugging Face dataset as pending slot entries (no ingest yet).
     * Train split → dataset card; test split → benchmark card.
     */
    const pickFromHuggingFace = useCallback(
        async (pick) => {
            const datasetId = pick?.datasetId;
            if (!datasetId) return null;
            const initialColumns = Array.isArray(pick.columns) ? pick.columns : [];
            const needsColumnFetch = !initialColumns.length && Boolean(usecase?.client_id);

            const trainSplit = pick.trainSplit || null;
            const testSplit = pick.testSplit || null;
            const result = {
                datasetId,
                columns: initialColumns,
                datasetEntryId: null,
                benchmarkEntryId: null,
            };

            const attach = (target, split, uploadName) => {
                const id = newEntryId();
                setSlots((prev) => ({
                    ...prev,
                    [target]: {
                        files: [
                            ...prev[target].files,
                            {
                                ...emptyFileEntry,
                                id,
                                uploadName,
                                fromHuggingFace: true,
                                huggingFaceDatasetId: datasetId,
                                huggingFaceSplit: split,
                                huggingFaceTrainSplit: trainSplit,
                                huggingFaceTestSplit: testSplit,
                                columns: initialColumns,
                                columnsLoading: needsColumnFetch,
                            },
                        ],
                    },
                }));
                return id;
            };

            if (trainSplit) {
                result.datasetEntryId = attach('dataset', trainSplit, datasetId);
            }
            if (testSplit) {
                result.benchmarkEntryId = attach('benchmark', testSplit, `${datasetId}_test`);
            }

            const jobs = [];
            if (needsColumnFetch) {
                jobs.push(
                    (async () => {
                        try {
                            const columns = await fetchHfDatasetColumns({
                                datasetId,
                                clientId: usecase.client_id,
                            });
                            result.columns = columns;
                            if (result.datasetEntryId) {
                                patchEntry('dataset', result.datasetEntryId, {
                                    columns,
                                    columnsLoading: false,
                                });
                            }
                            if (result.benchmarkEntryId) {
                                patchEntry('benchmark', result.benchmarkEntryId, {
                                    columns,
                                    columnsLoading: false,
                                });
                            }
                        } catch (error) {
                            console.error('Error fetching Hugging Face columns:', error);
                            sparkToast.error(error?.message || 'Failed to fetch dataset columns');
                            if (result.datasetEntryId) {
                                patchEntry('dataset', result.datasetEntryId, {
                                    columnsLoading: false,
                                });
                            }
                            if (result.benchmarkEntryId) {
                                patchEntry('benchmark', result.benchmarkEntryId, {
                                    columnsLoading: false,
                                });
                            }
                        }
                    })()
                );
            }
            if (usecase?.client_id) {
                const loadSamples = async (target, entryId, split) => {
                    if (!entryId || !split) return;
                    try {
                        const { samples, sample } = await fetchHfDatasetSamples({
                            datasetId,
                            clientId: usecase.client_id,
                            split,
                            limit: HF_PREVIEW_SAMPLE_LIMIT,
                        });
                        let records = samples;
                        if (!records.length && sample && Object.keys(sample).length) {
                            records = [sample];
                        }
                        if (!records.length) return;
                        patchEntry(target, entryId, {
                            sampleRecord: records[0],
                            sampleRecords: records,
                            previewSampleLimit: HF_PREVIEW_SAMPLE_LIMIT,
                        });
                    } catch (error) {
                        console.error('Error fetching Hugging Face samples:', error);
                    }
                };
                jobs.push(loadSamples('dataset', result.datasetEntryId, trainSplit));
                jobs.push(loadSamples('benchmark', result.benchmarkEntryId, testSplit));
            }
            if (jobs.length) {
                await Promise.all(jobs);
            }
            return result;
        },
        [patchEntry, usecase?.client_id]
    );

    /**
     * Remove one pending entry by id, or clear every pending entry when
     * `entryId` is omitted (e.g. after detaching uploaded names from the
     * usecase).
     */
    const removeFile = useCallback((target, entryId) => {
        setSlots((prev) => ({
            ...prev,
            [target]: {
                files: entryId
                    ? prev[target].files.filter((entry) => entry.id !== entryId)
                    : [],
            },
        }));
    }, []);

    const removeFiles = useCallback((target, entryIds) => {
        const idSet = new Set(entryIds || []);
        if (!idSet.size) return;
        setSlots((prev) => ({
            ...prev,
            [target]: {
                files: prev[target].files.filter((entry) => !idSet.has(entry.id)),
            },
        }));
    }, []);

    /**
     * Upload every picked file / clone every library pick through the shared
     * two-phase flow. `getMetadata(target, entry)` returns complete-dataset-upload
     * (or clone) metadata for that file (per-file in split-details mode).
     */
    const submit = useCallback(
        async ({ getMetadata, onUploadSuccess }) => {
            setIsSubmitting(true);
            try {
                const uploadedHfIds = new Set();
                for (const target of ['dataset', 'benchmark']) {
                    for (const entry of slots[target].files) {
                        const metadata = getMetadata(target, entry);
                        if (entry.fromHuggingFace && entry.huggingFaceDatasetId) {
                            const datasetId = entry.huggingFaceDatasetId;
                            if (uploadedHfIds.has(datasetId)) continue;
                            uploadedHfIds.add(datasetId);
                            const hfMetadata = entry.huggingFaceTrainSplit
                                ? getMetadata('dataset', entry)
                                : metadata;
                            await uploadHuggingFaceDatasetToTask({
                                datasetId,
                                trainSplit: entry.huggingFaceTrainSplit,
                                testSplit: entry.huggingFaceTestSplit,
                                metadata: hfMetadata,
                                usecase,
                                taskType,
                                setProgressStatus,
                                clearCancelledUploadSuppression,
                                refreshUsecases,
                                onComplete: onUploadSuccess,
                            });
                            continue;
                        }
                        if (entry.fromLibrary && entry.librarySourceName) {
                            const importWithoutLabels = Boolean(
                                !metadata.labelColumnName &&
                                    !metadata.generateResponsesWithAi &&
                                    (!metadata.labelMappings ||
                                        Object.keys(metadata.labelMappings).length === 0)
                            );
                            await cloneLibraryDatasetToTask({
                                datasetName: entry.librarySourceName,
                                isBenchmark: target === 'benchmark',
                                columnMappings: {
                                    example_column_name: metadata.exampleColumnName || undefined,
                                    label_column_name: metadata.labelColumnName || undefined,
                                    // Multilabel classification labels delimiter.
                                    classification_labels_delimiter:
                                        metadata.delimiter || undefined,
                                    // "Data was created by" — persisted on the
                                    // clone so draft resume restores the choice.
                                    data_creation_type:
                                        metadata.datasetOrigin &&
                                        metadata.datasetOrigin !== 'skip'
                                            ? metadata.datasetOrigin
                                            : undefined,
                                    label_mappings:
                                        metadata.labelMappings &&
                                        Object.keys(metadata.labelMappings).length > 0
                                            ? metadata.labelMappings
                                            : undefined,
                                    import_without_labels: importWithoutLabels || undefined,
                                    empty_multilabel_handling:
                                        metadata.labelColumnName
                                            ? metadata.emptyMultilabelHandling || undefined
                                            : undefined,
                                    generate_responses_with_ai:
                                        metadata.generateResponsesWithAi || undefined,
                                    defer_label_generation:
                                        metadata.deferLabelGeneration || undefined,
                                    // Agentic tasks only (undefined otherwise).
                                    generate_trajectories:
                                        metadata.generateTrajectories || undefined,
                                    empty_trajectory_handling:
                                        metadata.emptyTrajectoryHandling || undefined,
                                },
                                currentUsecase: usecase,
                                subGroup: currentSubGroup,
                                setProgressStatus,
                                updateUsecase,
                                registerUploadGroup,
                                clearCancelledUploadSuppression,
                                refreshUsecases,
                                onComplete: onUploadSuccess,
                            });
                            continue;
                        }
                        if (!entry.file) continue;
                        const originalName = entry.file.name.split('.')[0];
                        await uploadDatasetFile({
                            file: entry.file,
                            type: target,
                            currentUsecase: usecase,
                            selectedTaskType: taskType,
                            renamedFileName:
                                entry.uploadName && entry.uploadName !== originalName
                                    ? entry.uploadName
                                    : null,
                            ...metadata,
                            context: {
                                setProgressStatus,
                                updateUsecase,
                                registerUploadAbort,
                                unregisterUploadAbort,
                                registerUploadGroup,
                                clearCancelledUploadSuppression,
                                currentSubGroup,
                            },
                            callbacks: { onUploadSuccess },
                        });
                    }
                }
                // Drop File handles so navigating Next→Back does not
                // re-upload, but keep uploadName/columns/content so the
                // details forms (Languages, Review Examples, …) still render.
                // Library picks are cleared so Done→reopen does not re-clone.
                const keepUploadedEntry = (entry) => {
                    if (entry.fromLibrary || entry.fromHuggingFace) return null;
                    if (!entry.file) return entry;
                    const cloneName =
                        usecase?._id && entry.uploadName
                            ? predictedCloneNameForUsecase(
                                  String(entry.uploadName).slice(0, CLONE_SOURCE_BASE_MAX_LEN),
                                  usecase._id
                              )
                            : null;
                    return {
                        ...entry,
                        file: null,
                        datasetName: entry.datasetName || cloneName,
                    };
                };
                setSlots((prev) => ({
                    dataset: {
                        files: prev.dataset.files.map(keepUploadedEntry).filter(Boolean),
                    },
                    benchmark: {
                        files: prev.benchmark.files.map(keepUploadedEntry).filter(Boolean),
                    },
                }));
            } finally {
                setIsSubmitting(false);
            }
        },
        [
            slots,
            usecase,
            taskType,
            setProgressStatus,
            updateUsecase,
            registerUploadAbort,
            unregisterUploadAbort,
            registerUploadGroup,
            clearCancelledUploadSuppression,
            currentSubGroup,
            refreshUsecases,
        ]
    );

    const attachExistingEntries = useCallback((target, entries) => {
        const incoming = (entries || []).filter(Boolean);
        if (!incoming.length) return;
        setSlots((prev) => {
            const existing = prev[target]?.files || [];
            const covered = new Set(
                existing.flatMap((entry) =>
                    [entry.id, entry.uploadName, entry.datasetName]
                        .filter(Boolean)
                        .flatMap((name) => [name, stripCloneSuffix(name) || name])
                )
            );
            const next = incoming.filter((entry) => {
                const keys = [entry.id, entry.uploadName, entry.datasetName].filter(Boolean);
                return !keys.some((key) => covered.has(key) || covered.has(stripCloneSuffix(key) || key));
            });
            if (!next.length) return prev;
            return {
                ...prev,
                [target]: { files: [...existing, ...next] },
            };
        });
    }, []);

    return {
        slots,
        pickFile,
        pickFiles,
        pickFromLibrary,
        pickFromHuggingFace,
        attachExistingEntries,
        removeFile,
        removeFiles,
        submit,
        isSubmitting,
    };
}
